import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { userAPI } from '../../api';
import { formatDate } from '../../utils/dateUtils';

const CommentsContainer = styled.div`
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 30px;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  margin-bottom: 20px;
  color: #333;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
`;

const CommentsList = styled.div`
  margin-top: 20px;
`;

const CommentItem = styled.div`
  padding: 15px;
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
  }
`;

const CommentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const PostInfo = styled.div`
  font-size: 14px;
  color: #666;
`;

const PostLink = styled(Link)`
  color: #333;
  font-weight: 500;
  text-decoration: none;
  
  &:hover {
    color: #ef4444;
    text-decoration: underline;
  }
`;

const BoardLink = styled(Link)`
  color: #666;
  margin-left: 8px;
  font-size: 13px;
  
  &:hover {
    color: #ef4444;
  }
  
  &:before {
    content: '[';
  }
  
  &:after {
    content: ']';
  }
`;

const CommentDate = styled.div`
  font-size: 12px;
  color: #888;
`;

const CommentContent = styled.div`
  font-size: 14px;
  color: #333;
  line-height: 1.5;
  margin-top: 5px;
  word-break: break-all;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`;

interface PageButtonProps {
  isActive: boolean;
}

const PageButton = styled.button<PageButtonProps>`
  padding: 5px 10px;
  margin: 0 5px;
  background-color: ${props => props.isActive ? '#ef4444' : '#f5f5f5'};
  color: ${props => props.isActive ? 'white' : '#333'};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background-color: ${props => props.isActive ? '#dc2626' : '#e0e0e0'};
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 30px;
  color: #666;
`;

// 댓글 타입 정의
interface Comment {
  id: number;
  content: string;
  created_at: string;
  updated_at: string | null;
  post_id: number;
  post_title: string;
  board_name: string;
  board_id: number;
  parent_id: number | null;
  is_secret: boolean;
}

const Comments: React.FC = () => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState('');
  
  // 내 댓글 목록 조회
  const fetchMyComments = async () => {
    setLoading(true);
    try {
      const response = await userAPI.getMyComments(currentPage);
      const responseData = response.data;
      if (responseData && responseData.success) {
        setComments(responseData.comments || []);
        setTotalPages(responseData.pagination?.totalPages || 1);
      } else {
        setError(responseData?.message || '댓글을 불러오는데 실패했습니다.');
      }
    } catch (err: any) {
      setError(err.message || '오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };
  
  // 초기 데이터 로드 및 페이지 변경 시 재로드
  useEffect(() => {
    fetchMyComments();
  }, [currentPage]);
  
  // 페이지 이동
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  
  if (loading && comments.length === 0) {
    return <div>로딩 중...</div>;
  }
  
  if (error) {
    return <div>{error}</div>;
  }
  
  return (
    <CommentsContainer>
      <SectionTitle>내가 작성한 댓글</SectionTitle>
      
      {comments.length === 0 ? (
        <NoResults>작성한 댓글이 없습니다.</NoResults>
      ) : (
        <>
          <CommentsList>
            {comments.map((comment) => (
              <CommentItem key={comment.id}>
                <CommentHeader>
                  <PostInfo>
                    <PostLink to={`/posts/${comment.post_id}`}>
                      {comment.post_title}
                    </PostLink>
                    <BoardLink to={`/boards/${comment.board_id}`}>
                      {comment.board_name}
                    </BoardLink>
                  </PostInfo>
                  <CommentDate>{formatDate(comment.created_at)}</CommentDate>
                </CommentHeader>
                <CommentContent>
                  {comment.is_secret ? '비밀 댓글입니다.' : comment.content}
                </CommentContent>
              </CommentItem>
            ))}
          </CommentsList>
          
          {totalPages > 1 && (
            <Pagination>
              <PageButton
                disabled={currentPage === 1}
                isActive={false}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                이전
              </PageButton>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <PageButton
                  key={page}
                  isActive={currentPage === page}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </PageButton>
              ))}
              
              <PageButton
                disabled={currentPage === totalPages}
                isActive={false}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                다음
              </PageButton>
            </Pagination>
          )}
        </>
      )}
    </CommentsContainer>
  );
};

export default Comments; 